import { useEffect, useState } from 'react';
import { batchService } from '../../services/index.js';
import './SendCampaignModal.css';

const log = (...args) => console.log('[postcards/modal]', ...args);

export const SendCampaignModal = ({
  isOpen,
  selectedBatches,
  onClose,
  onConfirm,
  sending,
}) => {
  const [campaignName, setCampaignName] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [expandedAddresses, setExpandedAddresses] = useState({});
  const [loadingAddresses, setLoadingAddresses] = useState(false);

  useEffect(() => {
    if (isOpen) {
      log('opened', { selectedBatchCount: selectedBatches.length });
    }
  }, [isOpen, selectedBatches.length]);

  if (!isOpen) return null;

  const toggleExpand = async (batch) => {
    if (expandedId === batch._id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(batch._id);
    if (!expandedAddresses[batch._id]) {
      try {
        setLoadingAddresses(true);
        log('fetching batch addresses', batch._id);
        const res = await batchService.getBatchById(batch._id);
        const addresses = res?.data?.batch?.addresses || [];
        log('batch addresses fetched', {
          batchId: batch._id,
          count: addresses.length,
        });
        setExpandedAddresses((prev) => ({
          ...prev,
          [batch._id]: addresses,
        }));
      } catch (err) {
        console.error('[postcards/modal] ✗ error loading batch addresses', err);
        setExpandedAddresses((prev) => ({ ...prev, [batch._id]: [] }));
      } finally {
        setLoadingAddresses(false);
      }
    }
  };

  const totalAddresses = selectedBatches.reduce(
    (sum, b) => sum + (b?.totalAddressesSelected || 0),
    0
  );

  const canSend =
    campaignName.trim().length > 0 && selectedBatches.length > 0 && !sending;

  const handleClose = () => {
    if (sending) return;
    setCampaignName('');
    setExpandedId(null);
    setExpandedAddresses({});
    onClose();
  };

  return (
    <div className="modal-backdrop-sendcampaign" onClick={handleClose}>
      <div
        className="modal-content-sendcampaign"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header-sendcampaign">
          <h2 className="modal-title-sendcampaign">Send Campaign</h2>
          <button
            onClick={handleClose}
            className="modal-close-sendcampaign"
            disabled={sending}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="modal-body-sendcampaign">
          <div className="field-sendcampaign">
            <label className="label-sendcampaign">
              Campaign name <span className="required-sendcampaign">*</span>
            </label>
            <input
              type="text"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              placeholder="e.g., Spring 2026 - Zone A"
              className="input-sendcampaign"
              maxLength={200}
              disabled={sending}
              autoFocus
            />
          </div>

          <div className="batches-section-sendcampaign">
            <div className="batches-header-sendcampaign">
              <label className="label-sendcampaign">
                Batches to include ({selectedBatches.length})
              </label>
            </div>

            {selectedBatches.length === 0 ? (
              <div className="empty-sendcampaign">
                No batches selected. Close this dialog and pick the batches you
                want to send.
              </div>
            ) : (
              <div className="batches-list-sendcampaign">
                {selectedBatches.map((batch) => {
                  const isExpanded = expandedId === batch._id;
                  const addresses = expandedAddresses[batch._id];
                  return (
                    <div
                      key={batch._id}
                      className="batch-row-sendcampaign selected-sendcampaign"
                    >
                      <div className="batch-row-main-sendcampaign">
                        <span className="batch-number-sendcampaign">
                          {batch.batchNumber}
                        </span>
                        <span className="batch-meta-sendcampaign">
                          {batch.totalAddressesSelected} addresses
                        </span>
                        <span className="batch-meta-sendcampaign">
                          {new Date(batch.createdAt).toLocaleDateString()}
                        </span>
                        <button
                          type="button"
                          onClick={() => toggleExpand(batch)}
                          className="details-btn-sendcampaign"
                          disabled={sending}
                        >
                          {isExpanded ? 'Hide' : 'View details'}
                        </button>
                      </div>
                      {isExpanded && (
                        <div className="batch-details-sendcampaign">
                          {!addresses ? (
                            <div className="loading-sendcampaign">Loading…</div>
                          ) : addresses.length === 0 ? (
                            <div className="empty-sendcampaign">No addresses.</div>
                          ) : (
                            <div className="addresses-list-sendcampaign">
                              {addresses.slice(0, 25).map((a) => (
                                <div
                                  key={a._id || a.melissaAddressKey}
                                  className="address-item-sendcampaign"
                                >
                                  <span title={[a.addressLine1, a.addressLine2].filter(Boolean).join(' ')}>
                                    {a.addressLine1}
                                    {a.addressLine2 ? ` ${a.addressLine2}` : ''}
                                  </span>
                                  <span
                                    className="address-city-sendcampaign"
                                    title={`${a.city}, ${a.state} ${a.zipCode}`}
                                  >
                                    {a.city}, {a.state} {a.zipCode}
                                  </span>
                                </div>
                              ))}
                              {addresses.length > 25 && (
                                <div className="more-sendcampaign">
                                  …and {addresses.length - 25} more
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer-sendcampaign">
          <div className="summary-sendcampaign">
            {selectedBatches.length > 0 && (
              <>
                <strong>{selectedBatches.length}</strong> batch{selectedBatches.length === 1 ? '' : 'es'} •{' '}
                <strong>{totalAddresses}</strong> addresses
              </>
            )}
          </div>
          <div className="footer-actions-sendcampaign">
            <button
              onClick={handleClose}
              className="btn-cancel-sendcampaign"
              disabled={sending}
            >
              Cancel
            </button>
            <button
              onClick={() => {
                log('send button pressed', {
                  campaignName: campaignName.trim(),
                  batchIdsCount: selectedBatches.length,
                  totalAddresses,
                });
                onConfirm({
                  campaignName: campaignName.trim(),
                  batchIds: selectedBatches.map((b) => b._id),
                });
              }}
              className="btn-send-sendcampaign"
              disabled={!canSend}
            >
              {sending ? 'Sending…' : 'Send Campaign'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendCampaignModal;
